import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, Grid, Paper, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Pagination, Alert,
  CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, Avatar, useMediaQuery,
  alpha, Divider, LinearProgress, Tooltip,
  List, ListItem, ListItemAvatar, ListItemText, SwipeableDrawer,
  Stepper, Step, StepLabel, Tabs, Tab
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Computer as ComputerIcon,
  Memory as MemoryIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Devices as DevicesIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Chip as ChipIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  Monitor as MonitorIcon,
  SaveAlt as SaveAltIcon,
  Dns as DnsIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  HealthAndSafety as HealthIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import StatsCard from '../components/StatsCard';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

function Computadores() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [computadores, setComputadores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    cliente_id: '',
    nome_identificacao: '',
    tipo: 'desktop',
    marca: '',
    modelo: '',
    numero_serie: '',
    processador: '',
    memoria_ram: '',
    armazenamento: '',
    placa_video: '',
    placa_mae: '',
    fonte: '',
    sistema_operacional: '',
    observacoes: ''
  });

  const [filters, setFilters] = useState({
    busca: '',
    tipo: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    perPage: isMobile ? 5 : 10
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, nome: '' });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedComputador, setSelectedComputador] = useState(null);

  const totalComputadores = computadores.length;

  useEffect(() => {
    carregarDados();
  }, [pagination.page, filters.busca, filters.tipo]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [compRes, clientesRes] = await Promise.all([
        api.get('/computadores'),
        api.get('/clientes')
      ]);
      setComputadores(compRes.data || []);
      setClientes(clientesRes.data || []);
      setPagination(prev => ({ ...prev, total: compRes.data?.length || 0 }));
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    carregarDados();
  };

  const handleClearFilters = () => {
    setFilters({ busca: '', tipo: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const handleOpenDialog = (computador = null) => {
    if (computador) {
      setEditingId(computador.id);
      setFormData({
        cliente_id: computador.cliente_id || '',
        nome_identificacao: computador.nome_identificacao || '',
        tipo: computador.tipo || 'desktop',
        marca: computador.marca || '',
        modelo: computador.modelo || '',
        numero_serie: computador.numero_serie || '',
        processador: computador.processador || '',
        memoria_ram: computador.memoria_ram || '',
        armazenamento: computador.armazenamento || '',
        placa_video: computador.placa_video || '',
        placa_mae: computador.placa_mae || '',
        fonte: computador.fonte || '',
        sistema_operacional: computador.sistema_operacional || '',
        observacoes: computador.observacoes || ''
      });
    } else {
      setEditingId(null);
      setFormData({
        cliente_id: '',
        nome_identificacao: '',
        tipo: 'desktop',
        marca: '',
        modelo: '',
        numero_serie: '',
        processador: '',
        memoria_ram: '',
        armazenamento: '',
        placa_video: '',
        placa_mae: '',
        fonte: '',
        sistema_operacional: '',
        observacoes: ''
      });
    }
    setActiveTab(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const validateForm = () => {
    if (!formData.cliente_id) {
      toast.error('Selecione um cliente');
      return false;
    }
    if (!formData.nome_identificacao.trim()) {
      toast.error('Nome de identificação é obrigatório');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/computadores/${editingId}`, formData);
        toast.success('Computador atualizado com sucesso!');
      } else {
        await api.post('/computadores', formData);
        toast.success('Computador cadastrado com sucesso!');
      }
      handleCloseDialog();
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar computador');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id, nome) => {
    setDeleteDialog({ open: true, id, nome });
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/computadores/${deleteDialog.id}`);
      toast.success('Computador excluído com sucesso!');
      carregarDados();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir computador');
    } finally {
      setDeleteDialog({ open: false, id: null, nome: '' });
    }
  };

  const handleComputadorClick = (computador) => {
    if (isMobile) {
      setSelectedComputador(computador);
      setDrawerOpen(true);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedComputador(null);
  };

  const getTipoIcon = (tipo) => {
    switch(tipo) {
      case 'desktop': return <ComputerIcon />;
      case 'notebook': return <MonitorIcon />;
      case 'servidor': return <DnsIcon />;
      default: return <DevicesIcon />;
    }
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      desktop: 'Desktop',
      notebook: 'Notebook',
      servidor: 'Servidor',
      workstation: 'Workstation',
      allinone: 'All-in-One'
    };
    return labels[tipo] || tipo;
  };

  // Filtrar dados
  const dadosFiltrados = computadores.filter(item => {
    const busca = filters.busca.toLowerCase();
    const matchBusca = !busca || 
      item.nome_identificacao?.toLowerCase().includes(busca) ||
      item.cliente_nome?.toLowerCase().includes(busca) ||
      item.marca?.toLowerCase().includes(busca) ||
      item.modelo?.toLowerCase().includes(busca);
    const matchTipo = !filters.tipo || item.tipo === filters.tipo;
    return matchBusca && matchTipo;
  });

  // Paginação
  const startIndex = (pagination.page - 1) * pagination.perPage;
  const endIndex = startIndex + pagination.perPage;
  const itensPaginados = dadosFiltrados.slice(startIndex, endIndex);
  const totalPaginas = Math.ceil(dadosFiltrados.length / pagination.perPage);

  return (
    <Layout title="Computadores">
      <PageHeader
        title="Computadores"
        subtitle={`${totalComputadores} computadores cadastrados`}
        action={() => handleOpenDialog()}
        actionText={isMobile ? 'Novo' : 'Novo Computador'}
        icon={<ComputerIcon sx={{ color: '#39FF14' }} />}
      />

      {/* Cards de Estatísticas */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Total"
            value={totalComputadores}
            icon={<DevicesIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Desktop"
            value={computadores.filter(c => c.tipo === 'desktop').length}
            icon={<ComputerIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Notebook"
            value={computadores.filter(c => c.tipo === 'notebook').length}
            icon={<MonitorIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Servidores"
            value={computadores.filter(c => c.tipo === 'servidor').length}
            icon={<DnsIcon />}
            color="success"
          />
        </Grid>
      </Grid>

      <Card sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 3 }}>
        {/* Filtros */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={isMobile ? 1 : 2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Buscar computador"
                name="busca"
                value={filters.busca}
                onChange={handleFilterChange}
                placeholder="Nome, cliente, marca ou modelo"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filters.busca && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setFilters({ ...filters, busca: '' })}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'background.paper'
                  }
                }}
              />
            </Grid>
            <Grid item xs={8} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  name="tipo"
                  value={filters.tipo}
                  label="Tipo"
                  onChange={handleFilterChange}
                  sx={{ borderRadius: 3 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="desktop">Desktop</MenuItem>
                  <MenuItem value="notebook">Notebook</MenuItem>
                  <MenuItem value="servidor">Servidor</MenuItem>
                  <MenuItem value="workstation">Workstation</MenuItem>
                  <MenuItem value="allinone">All-in-One</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                sx={{
                  borderRadius: 3,
                  py: 1,
                  bgcolor: '#0A2647',
                  '&:hover': { bgcolor: '#1a3b5c' }
                }}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Lista Mobile vs Tabela Desktop */}
        {isMobile ? (
          // VERSÃO MOBILE
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : itensPaginados.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Nenhum computador encontrado
              </Alert>
            ) : (
              <AnimatePresence>
                {itensPaginados.map((comp, index) => (
                  <motion.div
                    key={comp.id}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card 
                      sx={{ 
                        mb: 1.5, 
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.06)',
                        '&:active': { transform: 'scale(0.98)' }
                      }}
                      onClick={() => handleComputadorClick(comp)}
                    >
                      <Box sx={{ p: 1.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar sx={{ 
                              bgcolor: alpha('#0A2647', 0.1),
                              color: '#0A2647',
                              width: 40,
                              height: 40
                            }}>
                              {getTipoIcon(comp.tipo)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                                {comp.nome_identificacao}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {comp.cliente_nome || 'Sem cliente'}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            label={getTipoLabel(comp.tipo)}
                            size="small"
                            sx={{ 
                              borderRadius: 2, 
                              fontWeight: 500,
                              bgcolor: alpha('#0A2647', 0.08),
                              color: '#0A2647'
                            }}
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                          <Box display="flex" gap={2}>
                            {comp.marca && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <BuildIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption">{comp.marca} {comp.modelo}</Typography>
                              </Box>
                            )}
                            {comp.memoria_ram && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <MemoryIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption">{comp.memoria_ram}</Typography>
                              </Box>
                            )}
                          </Box>
                          <Box>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDialog(comp);
                              }}
                              sx={{ color: '#0A2647' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(comp.id, comp.nome_identificacao);
                              }}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            <Tooltip title="Ficha Técnica">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/computadores/${comp.id}/ficha`);
                                }}
                                sx={{ color: '#2ecc71' }}
                              >
                                <HealthIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}

            {/* Paginação Mobile */}
            {totalPaginas > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPaginas}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2,
                      fontSize: '0.75rem'
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        ) : (
          // VERSÃO DESKTOP
          <>
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#0A2647', 0.04) }}>
                    <TableCell><strong>Computador</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell><strong>Marca/Modelo</strong></TableCell>
                    <TableCell><strong>CPU</strong></TableCell>
                    <TableCell><strong>RAM</strong></TableCell>
                    <TableCell><strong>Tipo</strong></TableCell>
                    <TableCell align="center"><strong>Ações</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : itensPaginados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          Nenhum computador encontrado
                        </Alert>
                      </TableCell>
                    </TableRow>
                  ) : (
                    itensPaginados.map((comp) => (
                      <TableRow 
                        key={comp.id} 
                        hover
                        sx={{
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha('#0A2647', 0.02)
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ 
                              bgcolor: alpha('#0A2647', 0.1),
                              color: '#0A2647',
                              width: 32,
                              height: 32
                            }}>
                              {getTipoIcon(comp.tipo)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {comp.nome_identificacao}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {comp.numero_serie || 'Sem série'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2">{comp.cliente_nome || '—'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {comp.marca} {comp.modelo}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {comp.sistema_operacional || 'SO não informado'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {comp.processador || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {comp.memoria_ram || '—'}
                          </Typography>
                          {comp.armazenamento && (
                            <Typography variant="caption" color="text.secondary">
                              {comp.armazenamento}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getTipoLabel(comp.tipo)}
                            size="small"
                            sx={{ 
                              borderRadius: 2,
                              bgcolor: alpha('#0A2647', 0.08),
                              color: '#0A2647',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(comp)}
                              sx={{ color: '#0A2647' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(comp.id, comp.nome_identificacao)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ficha Técnica">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/computadores/${comp.id}/ficha`)}
                              sx={{ color: '#2ecc71' }}
                            >
                              <HealthIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Paginação Desktop */}
            {totalPaginas > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPaginas}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Card>

      {/* Drawer Mobile - Detalhes do Computador */}
      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onOpen={() => {}}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '80vh',
            p: 2
          }
        }}
      >
        {selectedComputador && (
          <Box>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 2,
              pb: 1,
              borderBottom: '1px solid rgba(0,0,0,0.06)'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
                {selectedComputador.nome_identificacao}
              </Typography>
              <IconButton onClick={handleCloseDrawer} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Cliente</Typography>
                    <Typography variant="body2">{selectedComputador.cliente_nome || 'Não informado'}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <BuildIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Marca/Modelo</Typography>
                    <Typography variant="body2">{selectedComputador.marca} {selectedComputador.modelo}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SpeedIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Especificações</Typography>
                    <Typography variant="body2">
                      {selectedComputador.processador && `CPU: ${selectedComputador.processador}`}
                      {selectedComputador.memoria_ram && ` • RAM: ${selectedComputador.memoria_ram}`}
                      {selectedComputador.armazenamento && ` • HD: ${selectedComputador.armazenamento}`}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {selectedComputador.observacoes && (
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SaveAltIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Observações</Typography>
                      <Typography variant="body2">{selectedComputador.observacoes}</Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    handleCloseDrawer();
                    handleOpenDialog(selectedComputador);
                  }}
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: '#0A2647',
                    color: '#ffffff',
                    '&:hover': { 
                      bgcolor: '#1a3b5c',
                      color: '#ffffff'
                    }
                  }}
                >
                  Editar
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<HealthIcon />}
                  onClick={() => {
                    handleCloseDrawer();
                    navigate(`/computadores/${selectedComputador.id}/ficha`);
                  }}
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: '#2ecc71',
                    color: '#ffffff',
                    '&:hover': { 
                      bgcolor: '#27ae60',
                      color: '#ffffff'
                    }
                  }}
                >
                  Ficha Técnica
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    handleCloseDrawer();
                    handleDeleteClick(selectedComputador.id, selectedComputador.nome_identificacao);
                  }}
                  sx={{ 
                    borderRadius: 2,
                    color: '#d32f2f',
                    borderColor: '#d32f2f',
                    '&:hover': {
                      borderColor: '#d32f2f',
                      bgcolor: alpha('#d32f2f', 0.04)
                    }
                  }}
                >
                  Excluir
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </SwipeableDrawer>

      {/* MODAL DE CADASTRO/EDIÇÃO */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            overflow: 'hidden',
            boxShadow: '0 40px 80px rgba(0,0,0,0.2)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          }
        }}
      >
        {/* Header com gradiente e ícone */}
        <Box sx={{
          background: 'linear-gradient(135deg, #0A2647 0%, #1a3b5c 100%)',
          p: isMobile ? 2.5 : 3.5,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 200,
            height: 200,
            background: 'rgba(57, 255, 20, 0.04)',
            borderRadius: '50%'
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -80,
            left: -80,
            width: 250,
            height: 250,
            background: 'rgba(57, 255, 20, 0.03)',
            borderRadius: '50%'
          }} />

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            position: 'relative',
            zIndex: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 2 : 2.5 }}>
              <Avatar sx={{
                background: 'linear-gradient(135deg, rgba(57, 255, 20, 0.2) 0%, rgba(57, 255, 20, 0.05) 100%)',
                color: '#39FF14',
                width: isMobile ? 48 : 56,
                height: isMobile ? 48 : 56,
                border: '2px solid rgba(57, 255, 20, 0.2)'
              }}>
                {editingId ? <EditIcon sx={{ fontSize: isMobile ? 24 : 28 }} /> : <ComputerIcon sx={{ fontSize: isMobile ? 24 : 28 }} />}
              </Avatar>
              <Box>
                <Typography variant={isMobile ? "h5" : "h4"} sx={{ 
                  color: 'white', 
                  fontWeight: 700,
                  letterSpacing: '-0.5px'
                }}>
                  {editingId ? 'Editar Computador' : 'Novo Computador'}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: 'rgba(255,255,255,0.7)',
                  mt: 0.5
                }}>
                  {editingId 
                    ? 'Atualize as informações do equipamento' 
                    : 'Preencha os dados para cadastrar um novo computador'}
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={handleCloseDialog} 
              sx={{ 
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.05)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.15)',
                  transform: 'rotate(90deg)',
                  transition: 'transform 0.3s ease'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Stepper - apenas desktop */}
        {!isMobile && (
          <>
            <Box sx={{ px: 4, pt: 3 }}>
              <Stepper activeStep={activeTab} alternativeLabel>
                <Step>
                  <StepLabel>Dados Gerais</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Especificações</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Observações</StepLabel>
                </Step>
              </Stepper>
            </Box>
            <Divider />
          </>
        )}

        {/* Conteúdo do Formulário */}
        <DialogContent sx={{ 
          p: isMobile ? 3 : 4,
          overflow: 'auto'
        }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -30, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === 0 && (
                <Box>
                  {!isMobile && (
                    <Typography variant="subtitle2" sx={{ 
                      color: '#0A2647', 
                      fontWeight: 600,
                      mb: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontSize: '0.95rem'
                    }}>
                      <InfoIcon sx={{ fontSize: 20, color: '#0A2647' }} />
                      Informações Gerais
                    </Typography>
                  )}
                  <Grid container spacing={isMobile ? 2.5 : 3}>
                    <Grid item xs={12}>
                      <FormControl fullWidth size={isMobile ? "medium" : "small"} required>
                        <InputLabel sx={{ '&.Mui-focused': { color: '#0A2647' } }}>
                          Cliente *
                        </InputLabel>
                        <Select
                          name="cliente_id"
                          value={formData.cliente_id}
                          label="Cliente *"
                          onChange={handleInputChange}
                          sx={{ 
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(10, 38, 71, 0.2)'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0A2647'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0A2647',
                              borderWidth: 2
                            }
                          }}
                        >
                          <MenuItem value="">Selecione um cliente</MenuItem>
                          {clientes.map((cliente) => (
                            <MenuItem key={cliente.id} value={cliente.id}>
                              {cliente.nome_razao}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={7}>
                      <TextField
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                        label="Nome de Identificação *"
                        name="nome_identificacao"
                        value={formData.nome_identificacao}
                        onChange={handleInputChange}
                        placeholder="Ex: PC João, Notebook Sala 2"
                        required
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#0A2647' },
                            '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      <FormControl fullWidth size={isMobile ? "medium" : "small"}>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          name="tipo"
                          value={formData.tipo}
                          label="Tipo"
                          onChange={handleInputChange}
                          sx={{ 
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(10, 38, 71, 0.2)'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0A2647'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0A2647',
                              borderWidth: 2
                            }
                          }}
                        >
                          <MenuItem value="desktop">🖥️ Desktop</MenuItem>
                          <MenuItem value="notebook">💻 Notebook</MenuItem>
                          <MenuItem value="servidor">🖧 Servidor</MenuItem>
                          <MenuItem value="workstation">⚡ Workstation</MenuItem>
                          <MenuItem value="allinone">🖥️ All-in-One</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                        label="Marca"
                        name="marca"
                        value={formData.marca}
                        onChange={handleInputChange}
                        placeholder="Ex: Dell, HP, Lenovo"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#0A2647' },
                            '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                        label="Modelo"
                        name="modelo"
                        value={formData.modelo}
                        onChange={handleInputChange}
                        placeholder="Ex: OptiPlex, ProBook"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#0A2647' },
                            '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                        label="Número de Série"
                        name="numero_serie"
                        value={formData.numero_serie}
                        onChange={handleInputChange}
                        placeholder="Número de série do equipamento"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#0A2647' },
                            '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                        label="Sistema Operacional"
                        name="sistema_operacional"
                        value={formData.sistema_operacional}
                        onChange={handleInputChange}
                        placeholder="Ex: Windows 11, Ubuntu 22.04"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#0A2647' },
                            '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeTab === 1 && (
                <Box>
                  {!isMobile && (
                    <Typography variant="subtitle2" sx={{ 
                      color: '#0A2647', 
                      fontWeight: 600,
                      mb: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontSize: '0.95rem'
                    }}>
                      <SettingsIcon sx={{ fontSize: 20, color: '#0A2647' }} />
                      Especificações Técnicas
                    </Typography>
                  )}
                  <Grid container spacing={isMobile ? 2.5 : 3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                        label="Processador"
                        name="processador"
                        value={formData.processador}
                        onChange={handleInputChange}
                        placeholder="Ex: Intel i5-10400, Ryzen 7 5700X"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SpeedIcon sx={{ color: 'rgba(10, 38, 71, 0.5)', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#0A2647' },
                            '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                        label="Memória RAM"
                        name="memoria_ram"
                        value={formData.memoria_ram}
                        onChange={handleInputChange}
                        placeholder="Ex: 8GB, 16GB DDR4"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MemoryIcon sx={{ color: 'rgba(10, 38, 71, 0.5)', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#0A2647' },
                            '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                        label="Armazenamento"
                        name="armazenamento"
                        value={formData.armazenamento}
                        onChange={handleInputChange}
                        placeholder="Ex: 512GB SSD, 1TB HD"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <StorageIcon sx={{ color: 'rgba(10, 38, 71, 0.5)', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#0A2647' },
                            '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                        label="Placa de Vídeo"
                        name="placa_video"
                        value={formData.placa_video}
                        onChange={handleInputChange}
                        placeholder="Ex: GTX 1650, Integrated"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#0A2647' },
                            '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                        label="Placa Mãe"
                        name="placa_mae"
                        value={formData.placa_mae}
                        onChange={handleInputChange}
                        placeholder="Modelo da placa mãe"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#0A2647' },
                            '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size={isMobile ? "medium" : "small"}
                        label="Fonte"
                        name="fonte"
                        value={formData.fonte}
                        onChange={handleInputChange}
                        placeholder="Ex: 500W, 650W"
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#0A2647' },
                            '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  {!isMobile && (
                    <Typography variant="subtitle2" sx={{ 
                      color: '#0A2647', 
                      fontWeight: 600,
                      mb: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontSize: '0.95rem'
                    }}>
                      <SaveAltIcon sx={{ fontSize: 20, color: '#0A2647' }} />
                      Observações
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    size={isMobile ? "medium" : "small"}
                    label="Observações"
                    name="observacoes"
                    multiline
                    rows={isMobile ? 6 : 8}
                    value={formData.observacoes}
                    onChange={handleInputChange}
                    placeholder="Adicione informações adicionais sobre o computador..."
                    sx={{
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        '&:hover fieldset': { borderColor: '#0A2647' },
                        '&.Mui-focused fieldset': { borderColor: '#0A2647', borderWidth: 2 }
                      }
                    }}
                  />
                </Box>
              )}
            </motion.div>
          </AnimatePresence>
        </DialogContent>

        {/* Footer com Botões */}
        <DialogActions sx={{ 
          p: isMobile ? 2.5 : 3.5, 
          bgcolor: 'background.paper',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          gap: 1.5,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Box sx={{ 
            width: isMobile ? '100%' : 'auto',
            display: 'flex', 
            gap: 1,
            order: isMobile ? 2 : 1
          }}>
            {!isMobile && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => setActiveTab(Math.max(0, activeTab - 1))}
                  disabled={activeTab === 0}
                  sx={{ 
                    borderRadius: 2,
                    color: '#0A2647',
                    borderColor: '#0A2647',
                    '&:hover': {
                      borderColor: '#0A2647',
                      bgcolor: alpha('#0A2647', 0.04)
                    }
                  }}
                >
                  Anterior
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setActiveTab(Math.min(2, activeTab + 1))}
                  disabled={activeTab === 2}
                  sx={{ 
                    borderRadius: 2,
                    color: '#0A2647',
                    borderColor: '#0A2647',
                    '&:hover': {
                      borderColor: '#0A2647',
                      bgcolor: alpha('#0A2647', 0.04)
                    }
                  }}
                >
                  Próximo
                </Button>
              </>
            )}
          </Box>
          <Box sx={{ 
            width: isMobile ? '100%' : 'auto',
            display: 'flex', 
            gap: 1,
            order: isMobile ? 1 : 2
          }}>
            <Button 
              onClick={handleCloseDialog} 
              variant="outlined"
              fullWidth={isMobile}
              sx={{ 
                borderRadius: 2,
                color: '#0A2647',
                borderColor: '#0A2647',
                '&:hover': {
                  borderColor: '#0A2647',
                  bgcolor: alpha('#0A2647', 0.04)
                }
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving}
              fullWidth={isMobile}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              sx={{
                borderRadius: 2,
                px: isMobile ? 0 : 5,
                py: isMobile ? 1.5 : 1,
                bgcolor: '#0A2647',
                color: '#ffffff',
                '&:hover': { 
                  bgcolor: '#1a3b5c',
                  color: '#ffffff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(10,38,71,0.3)'
                },
                '&:disabled': {
                  color: '#ffffff',
                  bgcolor: alpha('#0A2647', 0.6)
                },
                transition: 'all 0.3s ease',
                fontSize: isMobile ? '1rem' : '0.875rem',
                fontWeight: 600
              }}
            >
              {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Cadastrar'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dialog de Exclusão */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, id: null, nome: '' })}
        PaperProps={{
          sx: { 
            borderRadius: 4,
            p: 2,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxWidth: isMobile ? '95%' : 'auto'
          }
        }}
      >
        <Box sx={{ textAlign: 'center', p: isMobile ? 1 : 2 }}>
          <Avatar sx={{
            bgcolor: 'rgba(211, 47, 47, 0.1)',
            color: '#d32f2f',
            width: isMobile ? 50 : 60,
            height: isMobile ? 50 : 60,
            mx: 'auto',
            mb: 2
          }}>
            <DeleteIcon sx={{ fontSize: isMobile ? 24 : 30 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Confirmar exclusão
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Deseja realmente excluir o computador <strong>{deleteDialog.nome}</strong>?
            <br />
            Esta ação não pode ser desfeita.
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'center',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <Button 
              onClick={() => setDeleteDialog({ open: false, id: null, nome: '' })}
              variant="outlined"
              fullWidth={isMobile}
              sx={{ 
                borderRadius: 2, 
                px: isMobile ? 0 : 4,
                color: '#0A2647',
                borderColor: '#0A2647'
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error" 
              variant="contained"
              fullWidth={isMobile}
              sx={{ 
                borderRadius: 2, 
                px: isMobile ? 0 : 4,
                color: '#ffffff',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(211,47,47,0.3)'
                }
              }}
            >
              Excluir
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Layout>
  );
}

export default Computadores;
